import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { getPayerProfile, type PayerProfile } from "@/services/stripe-payer-profile.service";
import { fetchVendorPaymentDues } from "./payment-dues.slice";
import type { PaymentDuesFilters } from "./payment-dues.entity";

export function useVendorPaymentDues(filters?: PaymentDuesFilters) {
  const dispatch = useAppDispatch();
  const { individualPaymentDues, subsPaymentDues, loading, error } = useAppSelector((s) => s.paymentDues);

  useEffect(() => {
    dispatch(fetchVendorPaymentDues(filters));
  }, [dispatch, filters?.fromDate, filters?.toDate, filters?.searchText]);

  const refresh = () => dispatch(fetchVendorPaymentDues(filters));

  return {
    individualPaymentDues,
    subsPaymentDues,
    loading,
    error,
    refresh,
  };
}

export function usePayerProfile() {
  const activeOrgId = useAppSelector((s) => s.organization.activeOrgId);
  const [profile, setProfile] = useState<PayerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!activeOrgId) {
      setLoading(false);
      return;
    }

    // Tag each request so a slow response from a previous org (or an older
    // refresh) cannot clobber the result of the latest one.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextProfile = await getPayerProfile();
      if (requestId !== requestIdRef.current) return;
      setProfile(nextProfile);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message ?? "Unable to load payer profile.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeOrgId]);

  useEffect(() => {
    // refresh is recreated when the active org changes, so this drops the
    // previous org's profile instead of showing it while the new one loads.
    setProfile(null);
    void refresh();
  }, [refresh]);

  return { profile, loading, error, refresh, setProfile };
}
